import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-signup-form',
  template: `
    <form [formGroup]="authForm" (ngSubmit)="handleSignup()" class="form">
      <div class="container">
        <span class="title">{{ 'Auth.form.emailInput' | translate }}</span>
        <div class="inner">
          <input
            type="email"
            [placeholder]="'Auth.form.placeholder' | translate"
            formControlName="email"
            name="email"
            class="input"
            [class.error]="isFieldInvalid('email')"
          />
        </div>
        <span class="error-message" *ngIf="isFieldInvalid('email')">
          {{ getErrorMessage('email') }}
        </span>
      </div>

      <div class="container">
        <span class="title">{{ 'Auth.form.passwordInput' | translate }}</span>
        <div class="input-wrapper">
          <input
            [type]="showPassword ? 'text' : 'password'"
            [placeholder]="'Auth.form.passwordInput' | translate"
            formControlName="password"
            name="password"
            class="input"
            [class.error]="isFieldInvalid('password')"
          />
          <button class="input-btn" type="button" (click)="togglePassword()">
            <app-eye></app-eye>
          </button>
        </div>
        <span class="error-message" *ngIf="isFieldInvalid('password')">
          {{ getErrorMessage('password') }}
        </span>
      </div>

      <div class="container">
        <span class="title">{{ 'Auth.form.confirmPassword' | translate }}</span>
        <div class="input-wrapper">
          <input
            [type]="showConfirmPassword ? 'text' : 'password'"
            [placeholder]="'Auth.form.confirmPassword' | translate"
            formControlName="confirmPassword"
            name="confirmPassword"
            class="input"
            [class.error]="isFieldInvalid('confirmPassword') || passwordMismatchError"
          />
          <button class="input-btn" type="button" (click)="toggleConfirmPassword()">
            <app-eye></app-eye>
          </button>
        </div>
        <span class="error-message" *ngIf="passwordMismatchError || isFieldInvalid('confirmPassword')">
          {{ passwordMismatchError || getErrorMessage('confirmPassword') }}
        </span>
      </div>

      <div class="btn-container">
        <app-button
          class="btn"
          [text]="'Auth.form.signupButton' | translate"
          type="submit"
          [isLoading]="isSubmitLoading">
        </app-button>
      </div>
    </form>
  `,
  styles: [
    `
      .container {
        margin-bottom: 16px;
      }

      .title {
        display: block;
        margin-bottom: 8px;
        font-family: 'Noto Sans SC';
        font-size: 14px;
        color: #ffffff;
      }

      .inner, .input-wrapper {
        position: relative;
        display: flex;
        align-items: center;
      }

      .input {
        width: 100%;
        padding: 8px 12px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 4px;
        background: transparent;
        color: #ffffff;
        font-size: 14px;
      }

      .input.error {
        border-color: var(--red);
      }

      .error-message {
        color: var(--red);
        font-size: 12px;
        margin-top: 4px;
        display: block;
      }

      .input-btn {
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 8px;
        position: absolute;
        right: 4px;
        color: rgba(255, 255, 255, 0.5);
      }

      .text-wrapper {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        flex-direction: row;
        margin-top: 5px;
      }

      .text {
        font-family: 'Noto Sans SC';
        font-size: 14px;
        font-weight: 400;
        line-height: 24px;
        letter-spacing: 0.01em;
        text-align: center;
        color: #ffffff66;
      }

      .link {
        font-family: 'Noto Sans SC';
        font-size: 14px;
        font-weight: 500;
        line-height: 24px;
        letter-spacing: 0.01em;
        text-align: center;
        color: var(--red);
        text-decoration: none;
        margin-left: 5px;
      }

      .btn-container {
        min-width: 300px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 24px;
      }

      .btn {
        flex: 1;
      }

      :host {
  .inner {
    min-width: 300px;
    height: 46px;
    padding: 12px 16px 12px 16px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.1);
    margin-top: 12px;
    width: 100%;
    display: flex;
    flex-direction: row;
  }

  .input {
    appearance: none;
    outline: none;
    border: none;
    font-size: 14px;
    color: var(--white);
    margin-left: 5px;
    background: transparent;
    width: 100%;

    &::placeholder {
      color: var(--grey);
      font-family: 'Noto Sans SC';
      font-size: 14px;
      font-weight: 400;
      line-height: 22px;
      text-align: left;
    }
  }

  .container {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
  }

  .title {
    font-family: 'Noto Sans SC';
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
    text-align: left;
    color: var(--white);
   }
   .input-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: row;
    background: rgba(255, 255, 255, 0.1);
    margin-top: 12px;
    width: 300px;
    height: 46px;
    padding: 12px 16px 12px 16px;
    border-radius: 8px;
  }

  .input-btn {
    appearance: none;
    outline: none;
    background: none;
    border: none;
    cursor: pointer;
  }

  .error {
    color: var(--red);
    font-size: 12px;
    margin-top: 4px;
    display: block;
    font-family: 'Noto Sans SC';
  }
 }
    `
  ],
})
export class SignupFormComponent implements OnInit {
  authForm!: FormGroup;
  isTranslationsReady = false;
  @Input() isSubmitLoading = false;
  @Output() onSubmit = new EventEmitter();
  showPassword = false;
  showConfirmPassword = false;
  private _passwordMismatchError: string | null = null;

  constructor(private fb: FormBuilder, private translate: TranslateService) {
    this.initForm();
    this.initTranslations();
  }

  private initForm() {
    this.authForm = this.fb.group({
      email: ['', [Validators.required, Validators.email, this.noSpecialSymbolValidator]],
      password: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(20), this.noWhitespaceValidator, this.noChineseValidator]],
      confirmPassword: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(20), this.noWhitespaceValidator, this.noChineseValidator]],
    }, { validators: this.passwordMatchValidator });
  }

  private passwordMatchValidator(g: FormGroup) {
    return g.get('password')?.value === g.get('confirmPassword')?.value
      ? null
      : { passwordMismatch: true };
  }

  private noWhitespaceValidator(control: FormControl) {
    const isWhitespace = (control.value || '').indexOf(' ') !== -1;
    return isWhitespace ? { whitespace: true } : null;
  }

  private noSpecialSymbolValidator(control: FormControl) {
    const emailDomainMatcher = (control.value || '').split('@');
    if (emailDomainMatcher.length > 1) {
      const specialSymbol = emailDomainMatcher[0].indexOf('.') !== -1 || emailDomainMatcher[0].indexOf('+') !== -1;
      if (emailDomainMatcher[1] === 'foxit.io') {
        return null;
      }
      return specialSymbol ? { specialSymbol: true } : null;
    }
    return null;
  }

  private initTranslations() {
    // Set default language
    this.translate.setDefaultLang('zh-CN');

    // Try to get language from localStorage first
    const savedLang = localStorage.getItem('selectedLanguage');
    if (savedLang) {
      this.translate.use(savedLang);
    } else {
      // Use browser language if available, otherwise use default
      const browserLang = this.translate.getBrowserLang();
      let lang = 'zh-CN'; // 默认使用简体中文

      if (browserLang) {
        if (browserLang.startsWith('zh')) {
          // 中文特殊处理，区分简体和繁体
          const fullLang = navigator.language; // 获取完整的语言代码，如 zh-CN, zh-TW
          lang = fullLang.includes('TW') || fullLang.includes('HK') ? 'zh-TW' : 'zh-CN';
        } else if (browserLang.startsWith('en')) {
          lang = 'en-US';
        } else if (browserLang.startsWith('ru')) {
          lang = 'ru-RU';
        }
      }

      this.translate.use(lang);
      localStorage.setItem('selectedLanguage', lang);
    }
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.authForm.get(fieldName);
    return field ? (field.invalid && (field.dirty || field.touched)) : false;
  }

  private noChineseValidator(control: FormControl) {
    const value = control.value || '';
    return /[\u4e00-\u9fa5]/.test(value) ? { chinese: true } : null;
  }

  getErrorMessage(field: string): string {
    const control = this.authForm.get(field);
    if (!control) return '';

    if (control.hasError('required')) {
      switch (field) {
        case 'email':
          return this.translate.instant('Common.Error.emailRequired');
        case 'password':
          return this.translate.instant('Common.Error.passwordRequired');
        case 'confirmPassword':
          return this.translate.instant('Common.Error.confirmPasswordRequired');
        default:
          return '';
      }
    }

    if (control.hasError('specialSymbol')) {
      return this.translate.instant('Common.Error.containSpecialSymbol');
    }

    if (control.hasError('chinese')) {
      return this.translate.instant('Common.Error.noChinese');
    }

    if (control.hasError('email')) {
      return this.translate.instant('Common.Error.emailInvalid');
    }

    if (control.hasError('minlength')) {
      return this.translate.instant('Common.Error.passwordMinLength');
    }

    if (control.hasError('maxlength')) {
      return this.translate.instant('Common.Error.passwordMaxLength');
    }

    if (control.hasError('chinese')) {
      return this.translate.instant('Common.Error.noChinese');
    }

    if (control.hasError('whitespace')) {
      return this.translate.instant('Common.Error.noWhitespace');
    }

    return '';
  }

  get passwordMismatchError(): string | null {
    if (this.authForm.hasError('passwordMismatch')) {
      return this.translate.instant('Common.Error.confirmPasswordMismatch');
    }
    return null;
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPassword(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  handleSignup(): void {
    if (this.authForm.valid && !this.passwordMismatchError && !this.isSubmitLoading) {
      this.onSubmit.emit(this.authForm.value);
    } else {
      Object.keys(this.authForm.controls).forEach(key => {
        const control = this.authForm.get(key);
        if (control) {
          control.markAsTouched();
        }
      });
    }
  }

  ngOnInit(): void {
    this.translate.get('Auth.form.emailInput').subscribe(() => {
      this.isTranslationsReady = true;
    });
  }
}
